import { jwtDecode, type JwtPayload } from "jwt-decode";
import api from "@/services/api";
import { fetchGroups } from "@/services/groups";
import isGroupInList from "@/utils/getGroups";
import Group from "@/types/usergroup";

export default {
  state: {
    user: localStorage.getItem("user"),
    token: localStorage.getItem("token"),
    baobahUser: JSON.parse(localStorage.getItem("baobahUser") || "null"),
    rtoken: localStorage.getItem("refresh_token"),
    groups: JSON.parse(localStorage.getItem("groups") || "null"),
  },
  getters: {
    getUser(state: { baobahUser: any }) {
      return state.baobahUser;
    },
    getToken(state: { token: any }) {
      return state.token;
    },
    isAuthenticated(state: { token: string | null }) {
      return !!state.token;
    },
    hasBaoBahUser(state: { baobahUser: any }) {
      return !!state.baobahUser;
    },
    isActive(state: { baobahUser: any }) {
      return !!state.baobahUser?.is_active;
    },
    getUserId(state: { user: any }) {
      return state.user;
    },
    getGroups(state: { groups: any }) {
      return state.groups;
    },
  },
  mutations: {
    setToken(state: { token: string | null }, token: string) {
      state.token = token;
      localStorage.setItem("token", token);
    },
    setRefreshToken(state: { rtoken: string | null }, token: string) {
      state.rtoken = token;
      localStorage.setItem("refresh_token", token);
    },
    clearTokens(state: { token: string | null; rtoken: string | null }) {
      state.token = null;
      state.rtoken = null;
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
    },
    setBaoBahUser(state: { baobahUser: any }, baobahUser: any) {
      state.baobahUser = baobahUser;
      localStorage.setItem("baobahUser", JSON.stringify(baobahUser));
    },
    setMyUser(state: { user: any }) {
      const user_id = getUserIdFromToken();
      state.user = user_id;
      localStorage.setItem("user", JSON.stringify(user_id));
    },
    setGroups(state: { groups: string | null }, groups: any) {
      state.groups = groups;
      localStorage.setItem("groups", JSON.stringify(groups));
    },
    clearMyUser(state: { user: any }) {
      state.user = null;
      localStorage.removeItem("user");
    },
    clearBaoBahUser(state: { baobahUser: any }) {
      state.baobahUser = null;
      localStorage.removeItem("baobahUser");
    },
    clearGroups(state: { groups: any }) {
      state.groups = null;
      localStorage.removeItem("groups");
    },
  },
  actions: {
    async loginUser(
      { commit, dispatch }: any,
      user: { username: string; password: string }
    ) {
      try {
        const resp = await api.post("login/", {
          username: user.username,
          password: user.password,
        });
        if (resp.status === 200) {
          const access_token = resp.data.access;
          const refresh_token = resp.data.refresh;
          commit("setToken", access_token);
          commit("setRefreshToken", refresh_token);
          localStorage.setItem("username", user.username);
          commit("setMyUser");
          await dispatch("setBaoBahUser");
          const mygroups = await fetchGroups(access_token);
          commit("setGroups", mygroups);
          return { success: true };
        }
      } catch (err: any) {
        console.error("Login failed:", err);
        return {
          success: false,
          message: err.response?.data?.detail || "Login failed",
        };
      }
    },
    logout({ commit }: any) {
      commit("clearTokens");
      commit("clearBaoBahUser");
      commit("clearMyUser");
      commit("clearGroups");
      localStorage.removeItem("username");
    },
    checkAuth({ commit, dispatch }: any) {
      const token = localStorage.getItem("token");
      if (token && token !== commit.state.token) {
        commit("setToken", token);
      }
      if (!isSignedIn()) {
        dispatch("logout");
      }
    },
    async setBaoBahUser({ commit }: any) {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Token de acesso não encontrado");

        const resp = await api.get("profile/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (resp.status === 200) {
          commit("setBaoBahUser", resp.data);
        }
      } catch (err) {
        console.error("Erro ao obter perfil do usuário:", err);
      }
    },
    checkGroup({ state }: any, groupName: string): boolean {
      if (!state.groups) {
        console.error("Grupos não estão carregados no estado.");
        return false;
      }
      const grupos: Group[] = Array.isArray(state.groups)
        ? state.groups
        : JSON.parse(state.groups || "[]");

      return isGroupInList(grupos, groupName);
    },
  },
};

function isSignedIn(): boolean {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const { exp: expiration } = jwtDecode<{ exp: number }>(token);
    const isExpired = !!expiration && Date.now() > expiration * 1000;
    return !isExpired;
  } catch (_) {
    return false;
  }
}

function getUserIdFromToken(): number {
  const token = localStorage.getItem("token");
  if (token) {
    type CustomJwtPayload = JwtPayload & { user_id: number };
    const payload: CustomJwtPayload = jwtDecode(token);
    return payload.user_id;
  } else {
    throw new Error("Token não encontrado");
  }
}
